/**
 * ReauthorizeAction — re-run the OAuth consent for an existing account without changing its id
 * (spec §7). Runs `useRudderstackOAuth.start({ kind: 'reauth', ... })` — the reauth arm the hook
 * already implements (task-12-corrections.md: that work shipped in `13e5602e` and is not touched
 * here) — and mirrors `AddRudderstackAccountDrawer`'s waiting/blocked/error UX for the same relay.
 * On completion it toasts *Re-authorized <name>* and invalidates both account query keys (shared
 * UI notes §1), exactly like `EditAccountDrawer` and `DeleteAccountDialog` do for their writes.
 *
 * It owns NO trigger (2026-09-10). The trigger is a **Re-authorize kebab menu item** at the row —
 * every surface builds it with the same `buildAccountRowActions` — and the host hands the dialog's
 * open state down as `confirmOpen` / `onConfirmOpenChange`. Three structural rules make that safe,
 * and all three are load-bearing:
 *
 *   1. **This component renders OUTSIDE the KebabMenu's subtree**, at the row, so its `Dialog` is not
 *      torn down when the menu closes on select. A `Dialog` rendered inside `DropdownMenuContent`
 *      unmounts with it and never opens.
 *   2. **It stays MOUNTED independent of menu open/close**, because the status surface below — the
 *      waiting callout, Cancel, and *Reopen the window* — must outlive the trigger. The Settings
 *      TABLE achieves that by mounting one per eligible row; the card view and `AccountField` cannot
 *      (a virtualized card unmounts when scrolled away), so they mount exactly ONE for whichever
 *      account is re-authorizing, outside the list. A menu item is gone the instant the menu closes; a re-authorization in flight is not.
 *      Without them, someone mid-flow has no sign it is running, no way to cancel, and no way to
 *      recover a consent popup that fell behind the main window. Do not move any of it into the menu.
 *   3. **In the Settings table it renders in the row's NAME cell, not the actions cell**
 *      (2026-09-10). The actions column is `ROW_ACTIONS_COLUMN` — 3.5rem, right-aligned, sized for a
 *      kebab — so a callout plus two buttons overflowed the table's right edge and clipped its own
 *      text (`e2e/rs-accounts/amf-v2-reauth-4-opener-waiting.png`). Widening that column is not the
 *      fix: it is shared by other lists, and growing it mid-flow reflows the whole table under the
 *      user. A per-row status belongs beside the row's identity anyway. The move is free because the
 *      `Dialog` portals to `document.body` and `confirmOpen` rides the row datum, so this component's
 *      position in the row changes nothing about either — and nothing remounts, which is what keeps
 *      the live popup handle, nonce, BroadcastChannel listener and abandon timer alive.
 *
 *      Hence the container's classes: `items-start` (the name cell is left-aligned — `items-end` was
 *      only ever inherited from the actions cell), a `max-w-xs` cap so a long `oauth.error` wraps
 *      instead of stretching the Name column, and `whitespace-normal` because the shared `TableCell`
 *      is `whitespace-nowrap` and would otherwise render the callout as one unbreakable line.
 *
 * An idle row renders nothing but the (closed) dialog: the status surface appears only once a flow is
 * live, so mounting this on every OAuth row costs the row no chrome.
 *
 * Admin gating is NOT here any more — it belongs to the trigger, which is where the user meets it:
 * the menu item renders disabled with `ADMIN_ONLY_REASON` (ui skill rule 10, never hidden), so a
 * caller who cannot write can never reach the dialog to open it.
 */
import { useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { toast } from 'sonner';

import { Button } from '@/_shared/components/ui/button';
import { Callout } from '@/_shared/components/ui/callout';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/_shared/components/ui/dialog';
import { useOrpc } from '@/_shared/hooks/useOrpc';

import { useRudderstackOAuth } from './useRudderstackOAuth';

interface ReauthorizeActionProps {
  account: { id: string; name: string; accountDefinitionName: string };
  surface: 'activation' | 'settings';
  /** Whether the confirm dialog is open. Owned by the ROW, so the menu closing cannot close it. */
  confirmOpen: boolean;
  onConfirmOpenChange: (open: boolean) => void;
  onReauthorized?: (account: { accountId: string; name: string }) => void;
}

export function ReauthorizeAction({
  account,
  surface,
  confirmOpen,
  onConfirmOpenChange,
  onReauthorized,
}: ReauthorizeActionProps): React.JSX.Element {
  const orpc = useOrpc();
  const queryClient = useQueryClient();
  const oauth = useRudderstackOAuth();

  useEffect(() => {
    if (oauth.status !== 'done' || !oauth.accountId) return;
    toast.success(`Re-authorized ${account.name}`);
    // All account-backed query keys, always (shared UI notes §11): settings, activation picker,
    // and audience detail bindings read the same accounts, so invalidating only the settings key
    // would leave a stale secretVersion visible there.
    void queryClient.invalidateQueries({ queryKey: orpc.settings.rudderstackAccounts.key() });
    void queryClient.invalidateQueries({ queryKey: orpc.audience.listActivationAccounts.key() });
    void queryClient.invalidateQueries({ queryKey: orpc.audience.listRudderstackWarehouseAccounts.key() });
    onReauthorized?.({ accountId: oauth.accountId, name: account.name });
    // Nothing unmounts this component the way closing a drawer would — reset so a second
    // re-authorize isn't left permanently disabled by a stale 'done' status.
    oauth.reset();
    // Fire once per completed flow; the deps that matter are status/accountId (AddRudderstackAccountDrawer
    // does the same for its own completion effect).
    // eslint-disable-next-line react-hooks/exhaustive-deps -- fire once on completion, per flow
  }, [oauth.status, oauth.accountId]);

  const busy = oauth.status === 'starting' || oauth.status === 'waiting';
  const showStatus = busy || oauth.status === 'blocked' || (oauth.status === 'error' && Boolean(oauth.error));

  /**
   * ORDER MATTERS, and this is now the ONLY place the popup-gesture rule lives: `oauth.start` opens
   * the popup SYNCHRONOUSLY at the top of its own body, so it must run before `onConfirmOpenChange`
   * schedules the dialog's close. A Radix close animation / portal teardown that ran first would
   * revoke the user-gesture the popup depends on. `start` is async only after that first line.
   *
   * This is precisely why the TRIGGER may now be an ordinary menu item, where it once could not be:
   * the trigger no longer opens the popup — it only opens this dialog — so the gesture that matters
   * belongs to *Continue to sign-in* below, which is a plain button in a dialog and nothing tears
   * down underneath it. Do not reorder these two lines, and do not let any caller call `oauth.start`
   * from a menu item's `onSelect` instead: that would put the popup back behind a Radix teardown, and
   * the whole reason this indirection exists would be gone. Verified live against mini on 2026-09-10
   * (`e2e/rs-accounts/amf-v2-reauth.mjs`) — that driver is the regression net for exactly this.
   */
  const confirmAndStart = (): void => {
    void oauth.start({
      kind: 'reauth',
      accountId: account.id,
      accountDefinitionName: account.accountDefinitionName,
      surface,
    });
    onConfirmOpenChange(false);
  };

  return (
    <>
      {/* The status surface. It lives in the ROW, never in the menu — see rules 2 and 3 in the header. */}
      {showStatus && (
        <div className="mt-2 flex max-w-xs flex-col items-start gap-2 whitespace-normal">
          <div className="flex items-center gap-2">
            {busy && <Button type="button" variant="secondary" onClick={oauth.cancel} label="Cancel" />}
            {oauth.status === 'waiting' && (
              <Button type="button" variant="secondary" onClick={oauth.reopen} label="Reopen the window" />
            )}
          </div>

          {oauth.status === 'waiting' && (
            <Callout variant="info">Waiting for the provider… finish signing in using the window that opened.</Callout>
          )}
          {oauth.status === 'blocked' && (
            <Callout variant="warning">
              Your browser blocked the sign-in window. Allow popups for Lookout and try again.
            </Callout>
          )}
          {oauth.status === 'error' && oauth.error && <Callout variant="error">{oauth.error}</Callout>}
        </div>
      )}

      {/* Re-authorizing REPLACES the stored credential for an account that is already delivering, and
          the consent screen it opens can silently land on a different provider identity than the one
          the account was granted under. Confirm first (#11) — a `Dialog`, never `window.confirm`.
          Rendered here, at the row, and NOT inside the kebab: Radix unmounts a menu's content on
          close, so a dialog nested in it would be destroyed by the very click that opened it. */}
      <Dialog open={confirmOpen} onOpenChange={onConfirmOpenChange}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Re-authorize this account?</DialogTitle>
            <DialogDescription>
              {account.name} will be re-connected with whichever account you sign in as, replacing its current
              credential. Deliveries using it keep running in the meantime.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button type="button" variant="secondary" onClick={() => onConfirmOpenChange(false)} label="Cancel" />
            {/* NOT a second control named "Re-authorize": this label says what actually happens next,
                which is what a screen-reader or voice-control user needs from the one button that
                really starts the flow — and it keeps the confirm distinct from the menu item that
                asked the question. */}
            <Button type="button" variant="primary" onClick={confirmAndStart} label="Continue to sign-in" />
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
